import * as React from "react";
import classNames from "classnames";
import { Dispatch } from "redux";
import { connect } from "react-redux";
import { StoreState } from "../reducers/root-reducer";
import { WithStyles, Theme, createStyles, withStyles } from "@material-ui/core";
import { InjectedTranslateProps, translate } from "react-i18next";
import { Link, LinkProps, withRouter } from "react-router-dom";

import DrawerCore from "@material-ui/core/Drawer";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import Divider from "@material-ui/core/Divider";
import Typography from "@material-ui/core/Typography";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";

import SettingsIcon from "@material-ui/icons/Settings";
import PrintIcon from "@material-ui/icons/Print";
import BuildIcon from "@material-ui/icons/Build";
import TableChartIcon from "@material-ui/icons/TableChart";
import ListIcon from "@material-ui/icons/List";
import AddCommentIcon from "@material-ui/icons/AddComment";
import AssessmentIcon from "@material-ui/icons/Assessment";
import MoneyIcon from "@material-ui/icons/AttachMoney";
import grey from "@material-ui/core/colors/grey";
import { closeDrawer } from "../actions/store/ui-actions";
import { Capabilities } from "../integration/capabilities";
import { AuthInfo } from "../api/token-storage";
import { InjectedRouterProps } from "../router/props";
import { VERSION } from "../environments/version";
import { Routes } from "../router/routes";
import { isIPhoneX } from '../util/device-util';

interface State {}

export interface DrawerProps {}

interface ConnectedDrawerProps extends WithStyles<typeof styles> {
	open: boolean;
	capabilities: Capabilities;
	authInfo: AuthInfo;
	layout: "desktop" | "tablet" | "phone";
	close: () => void;
}

type Props = DrawerProps &
	ConnectedDrawerProps &
	InjectedTranslateProps &
	InjectedRouterProps;

export class Drawer extends React.Component<Props, State> {
	private close() {
		const { open, close } = this.props;

		if (open) {
			close();
		}
	}

	private handleListItemClick = () => {
		this.close();
	};

	private handleClickAway = (event: React.MouseEvent<any>) => {
		let shouldClose = true;

		(event as any).composedPath().forEach((node: Element) => {
			if (node.id === "toggle-drawer-button") {
				shouldClose = false;
			}
		});

		if (shouldClose) {
			this.close();
		}
	};

	public render() {
		const {
			classes,
			t,
			open,
			capabilities,
			authInfo,
			location,
			layout
		} = this.props;

		const DashboardLink = (props: LinkProps) => (
			<Link to={Routes.Dashboard} {...props} />
		);
		const BookingPlanLink = (props: LinkProps) => (
			<Link to={Routes.BookingPlanComposer} {...props} />
		);
		const BookingPlanListLink = (props: LinkProps) => (
			<Link to={Routes.BookingPlanList} {...props} />
		);
		const InquiryListLink = (props: LinkProps) => (
			<Link to={Routes.InquiryList} {...props} />
		);
		const PaymentsLink = (props: LinkProps) => (
			<Link to={Routes.Payments} {...props} />
		);
		const PrintviewsLink = (props: LinkProps) => (
			<Link to={Routes.PrintViews} {...props} />
		);
		const AdminAreaLink = (props: LinkProps) => (
			<Link to={Routes.AdminSettings} {...props} />
		);
		const SettingsLink = (props: LinkProps) => (
			<Link to={Routes.Settings} {...props} />
		);

		return (
			<ClickAwayListener onClickAway={this.handleClickAway}>
				<DrawerCore
					variant="persistent"
					open={true}
					hideBackdrop={true}
					className={classNames(classes.root, {
						[classes.rootOpen]: open
					})}
					classes={{
						paper: classNames(classes.drawerPaper)
					}}
				>
					<div className={classes.toolbar} />
					<Divider className={classes.contrastDivider} />
					<List component="nav">
						<ListItem
							dense
							button
							classes={{
								button: classNames(classes.contrastButton, {
									[classes.contrastButtonActive]:
										location.pathname === Routes.Dashboard
								})
							}}
							component={DashboardLink}
							onClick={this.handleListItemClick}
						>
							<ListItemIcon>
								<AssessmentIcon className={classes.contrast} />
							</ListItemIcon>
							<ListItemText
								classes={{ primary: classes.contrast }}
								primary={t("navigation.dashboard")}
							/>
						</ListItem>
					</List>
					<Divider className={classes.contrastDivider} />
					{layout !== "phone" && (
						<React.Fragment>
							<List component="nav">
								<ListItem
									dense
									button
									classes={{
										button: classNames(
											classes.contrastButton,
											{
												[classes.contrastButtonActive]:
													location.pathname ===
													Routes.BookingPlanComposer
											}
										)
									}}
									component={BookingPlanLink}
									onClick={this.handleListItemClick}
								>
									<ListItemIcon>
										<TableChartIcon
											className={classes.contrast}
										/>
									</ListItemIcon>
									<ListItemText
										classes={{ primary: classes.contrast }}
										primary={t("navigation.bookingPlan")}
									/>
								</ListItem>
							</List>
						</React.Fragment>
					)}
					<Divider className={classes.contrastDivider} />
					<List component="nav">
						<ListItem
							dense
							button
							classes={{
								button: classNames(classes.contrastButton, {
									[classes.contrastButtonActive]:
										location.pathname ===
										Routes.BookingPlanList
								})
							}}
							component={BookingPlanListLink}
							onClick={this.handleListItemClick}
						>
							<ListItemIcon>
								<ListIcon className={classes.contrast} />
							</ListItemIcon>
							<ListItemText
								classes={{ primary: classes.contrast }}
								primary={t("navigation.bookingPlanList")}
							/>
						</ListItem>
					</List>
					<Divider className={classes.contrastDivider} />
					<List component="nav">
						<ListItem
							dense
							button
							classes={{
								button: classNames(classes.contrastButton, {
									[classes.contrastButtonActive]:
										location.pathname === Routes.InquiryList
								})
							}}
							component={InquiryListLink}
							onClick={this.handleListItemClick}
						>
							<ListItemIcon>
								<AddCommentIcon className={classes.contrast} />
							</ListItemIcon>
							<ListItemText
								classes={{ primary: classes.contrast }}
								primary={t("navigation.inquiryList")}
							/>
						</ListItem>
					</List>
					<Divider className={classes.contrastDivider} />
					<List component="nav">
						<ListItem
							dense
							button
							classes={{
								button: classNames(classes.contrastButton, {
									[classes.contrastButtonActive]:
										location.pathname === Routes.Payments
								})
							}}
							component={PaymentsLink}
							onClick={this.handleListItemClick}
						>
							<ListItemIcon>
								<MoneyIcon className={classes.contrast} />
							</ListItemIcon>
							<ListItemText
								classes={{ primary: classes.contrast }}
								primary={t("navigation.payments")}
							/>
						</ListItem>
					</List>
					<Divider className={classes.contrastDivider} />
					<Divider className={classes.contrastDivider} />
					<List component="nav">
						<ListItem
							dense
							button
							classes={{
								button: classNames(classes.contrastButton, {
									[classes.contrastButtonActive]:
										location.pathname === Routes.PrintViews
								})
							}}
							component={PrintviewsLink}
							onClick={this.handleListItemClick}
						>
							<ListItemIcon>
								<PrintIcon className={classes.contrast} />
							</ListItemIcon>
							<ListItemText
								classes={{ primary: classes.contrast }}
								primary={t("navigation.printviews")}
							/>
						</ListItem>
					</List>
					<Divider className={classes.contrastDivider} />
					<div className={classes.flexExpand} />

					{authInfo.isAdmin && (
						<React.Fragment>
							<Divider className={classes.contrastDivider} />
							<List component="nav">
								<ListItem
									dense
									button
									classes={{
										button: classNames(
											classes.contrastButton,
											{
												[classes.contrastButtonActive]:
													location.pathname ===
													Routes.AdminSettings
											}
										)
									}}
									component={AdminAreaLink}
									onClick={this.handleListItemClick}
								>
									<ListItemIcon>
										<BuildIcon
											className={classes.contrast}
										/>
									</ListItemIcon>
									<ListItemText
										classes={{ primary: classes.contrast }}
										primary={t("navigation.adminArea")}
									/>
								</ListItem>
							</List>
						</React.Fragment>
					)}

					<Divider className={classes.contrastDivider} />
					<List component="nav">
						<ListItem
							dense
							button
							classes={{
								button: classNames(classes.contrastButton, {
									[classes.contrastButtonActive]:
										location.pathname === Routes.Settings
								})
							}}
							component={SettingsLink}
							onClick={this.handleListItemClick}
						>
							<ListItemIcon>
								<SettingsIcon className={classes.contrast} />
							</ListItemIcon>
							<ListItemText
								classes={{ primary: classes.contrast }}
								primary={t("navigation.settings")}
							/>
						</ListItem>
					</List>
					<Divider className={classes.contrastDivider} />
					<div className={classes.versionInfo}>
						<Typography className={classes.versionInfoText}>
							Tareso v{VERSION}
						</Typography>
					</div>
				</DrawerCore>
			</ClickAwayListener>
		);
	}
}

const mapStateToProps = (state: StoreState) => ({
	open: state.uiDrawerOpen,
	capabilities: state.capabilities,
	authInfo: state.authInfo,
	layout: state.layout
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	close: () => dispatch(closeDrawer())
});

const styles = (theme: Theme) =>
	createStyles({
		root: {
			position: "fixed",
			top: 0,
			left: 0,
			zIndex: theme.zIndex.drawer,
			transform: "translate3d(-100%, 0, 0)",
			transition: "transform 0.3s cubic-bezier(1, 0, 0, 1)"
		},
		rootOpen: {
			transform: "translate3d(0, 0, 0)"
		},
		toolbar: {
			...theme.mixins.toolbar,
			minHeight: 48
		},
		drawerPaper: {
			position: "relative",
			whiteSpace: "nowrap",
			background: grey[800],
			width: 240,
			transition: theme.transitions.create("width", {
				easing: theme.transitions.easing.sharp,
				duration: theme.transitions.duration.enteringScreen
			})
		},
		drawerPaperClosed: {
			overflowX: "hidden",
			transition: theme.transitions.create("width", {
				easing: theme.transitions.easing.sharp,
				duration: theme.transitions.duration.leavingScreen
			}),
			width: theme.spacing.unit * 7,
			[theme.breakpoints.up("sm")]: {
				width: theme.spacing.unit * 9
			}
		},
		flexExpand: {
			flexGrow: 1
		},
		expandButton: {
			justifyContent: "flex-end"
		},
		expandButtonClosed: {
			justifyContent: "flex-start"
		},
		contrast: {
			color: "white"
		},
		contrastButton: {
			color: "white",
			"&:hover": {
				backgroundColor: "rgba(255, 255, 255, 0.1)"
			}
		},
		contrastButtonActive: {
			backgroundColor: "rgba(255, 255, 255, 0.1)"
		},
		contrastDivider: {
			backgroundColor: "rgba(255, 255, 255, 0.12)"
		},
		versionInfo: {
			textAlign: "center",
			padding: 10,
			marginTop: 5,
			marginBottom: 5
		},
		versionInfoText: {
			color: "#999",
			fontSize: "0.8rem"
		}
	});

export const ConnectedDrawer = translate("translations")(
	withRouter(
		withStyles(styles)(
			connect(
				mapStateToProps,
				mapDispatchToProps
			)(Drawer)
		)
	)
);
